import React, { useCallback, useEffect, useState } from 'react';
import { X, Copy, Check } from 'lucide-react';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('wmf_token')}` });

// Generate-link buttons + history for the two student surveys. Kept inside
// this modal since "look up student -> view details -> send survey" is the
// whole flow for now -- there's no separate survey administration page yet.
const SurveyLinksSection = ({ studentId }) => {
  const [links, setLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(true);
  const [generating, setGenerating] = useState(null); // 'initial' | 'final' | null
  const [generatedLink, setGeneratedLink] = useState(null); // { surveyType, url }
  const [copied, setCopied] = useState(false);
  const [copiedTokenId, setCopiedTokenId] = useState(null);
  const [error, setError] = useState(null);

  const fetchLinks = useCallback(async () => {
    setLoadingLinks(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/surveys/links?studentId=${studentId}`,
        { headers: authHeaders() }
      );
      if (!response.ok) throw new Error('Failed to load survey links');
      const data = await response.json();
      setLinks(data.links || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingLinks(false);
    }
  }, [studentId]);

  useEffect(() => { fetchLinks(); }, [fetchLinks]);

  const handleGenerate = async (surveyType) => {
    setGenerating(surveyType);
    setError(null);
    setCopied(false);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/surveys/links`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ studentId, surveyType }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.msg || 'Failed to create survey link');
      }
      const data = await response.json();
      setGeneratedLink({ surveyType, url: data.url });
      await fetchLinks();
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(null);
    }
  };

  const handleCopy = async () => {
    if (!generatedLink) return;
    try {
      await navigator.clipboard.writeText(generatedLink.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Clipboard API can be unavailable (e.g. non-HTTPS); the link is still
      // visible on screen for the therapist to copy manually.
    }
  };

  const handleCopyRowLink = async (link) => {
    if (!link.url) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopiedTokenId(link.tokenId);
      setTimeout(() => setCopiedTokenId(null), 2000);
    } catch (err) {
      // Clipboard API can be unavailable (e.g. non-HTTPS).
    }
  };

  return (
    <div>
      <h4 className="text-sm font-medium text-slate-500 mb-2">Surveys</h4>

      <div className="flex gap-3 mb-3">
        <button
          onClick={() => handleGenerate('initial')}
          disabled={generating !== null}
          className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {generating === 'initial' ? 'Generating…' : 'Send Initial Survey'}
        </button>
        <button
          onClick={() => handleGenerate('final')}
          disabled={generating !== null}
          className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {generating === 'final' ? 'Generating…' : 'Send Final Survey'}
        </button>
      </div>

      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

      {generatedLink && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-xs text-blue-800 mb-1">
            {generatedLink.surveyType === 'initial' ? 'Initial' : 'Final'} survey link created — copy and send it to the student:
          </p>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={generatedLink.url}
              onFocus={(e) => e.target.select()}
              className="flex-1 text-xs bg-white border border-slate-300 rounded px-2 py-1 text-slate-700"
            />
            <button
              onClick={handleCopy}
              className="p-1.5 text-blue-700 hover:text-blue-900 border border-blue-300 rounded"
              title="Copy link"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </button>
          </div>
        </div>
      )}

      {loadingLinks ? (
        <p className="text-sm text-slate-500">Loading survey history…</p>
      ) : links.length > 0 ? (
        <div className="space-y-1">
          {links.map((link) => (
            <div key={link.tokenId} className="flex items-center justify-between gap-2 text-sm py-1 border-b border-slate-100 last:border-b-0">
              <span className="text-slate-700 capitalize">{link.surveyType} survey</span>
              <span className="text-slate-400 text-xs">
                {link.createdAt ? new Date(link.createdAt).toLocaleDateString() : ''}
              </span>
              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                link.isCompleted ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
              }`}>
                {link.isCompleted ? 'Completed' : link.url ? 'Pending' : 'Expired'}
              </span>
              {link.url && (
                <button
                  onClick={() => handleCopyRowLink(link)}
                  className="p-1 text-blue-700 hover:text-blue-900"
                  title="Copy link"
                >
                  {copiedTokenId === link.tokenId ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-slate-500">No surveys have been sent to this student yet.</p>
      )}
    </div>
  );
};

export const StudentDetailsModal = ({ student, onClose }) => {
  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl relative max-h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 flex-shrink-0">
          <h3 className="text-lg font-semibold text-slate-900">
            Student Details
          </h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 overflow-y-auto flex-1 min-h-0">
          {/* Basic Information */}
          <div>
            <h4 className="text-sm font-medium text-slate-500 mb-2">Basic Information</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-500">Name</label>
                <p className="text-sm text-slate-900">{student.firstName} {student.lastName}</p>
              </div>
              <div>
                <label className="text-xs text-slate-500">ID</label>
                <p className="text-sm text-slate-900">{student.id}</p>
              </div>
              <div>
                <label className="text-xs text-slate-500">Email</label>
                <p className="text-sm text-slate-900">{student.email || 'N/A'}</p>
              </div>
              <div>
                <label className="text-xs text-slate-500">Phone</label>
                <p className="text-sm text-slate-900">{student.phone || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Surveys */}
          <SurveyLinksSection studentId={student.id} />

          {/* Military Information */}
          <div>
            <h4 className="text-sm font-medium text-slate-500 mb-2">Military Information</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-500">Branch</label>
                <p className="text-sm text-slate-900">{student.militaryBranch}</p>
              </div>
              <div>
                <label className="text-xs text-slate-500">Affiliation</label>
                <p className="text-sm text-slate-900">{student.militaryAffiliation}</p>
              </div>
            </div>
          </div>

          {/* Music Information */}
          <div>
            <h4 className="text-sm font-medium text-slate-500 mb-2">Music Information</h4>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-slate-500">Desired Instruments</label>
                <p className="text-sm text-slate-900">{student.desiredInstruments || 'None specified'}</p>
              </div>
              <div>
                <label className="text-xs text-slate-500">Previous Experience</label>
                <p className="text-sm text-slate-900 whitespace-pre-line">
                  {student.previousExperience || 'No previous experience'}
                </p>
              </div>
              <div>
                <label className="text-xs text-slate-500">Goals</label>
                <p className="text-sm text-slate-900 whitespace-pre-line">
                  {student.goals || 'No goals specified'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 p-4 flex justify-end flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-slate-800 focus:outline-none"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Hook for fetching student details
export const useStudentDetails = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [studentDetails, setStudentDetails] = useState(null);

  const fetchDetails = async (studentId) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/students/${studentId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('wmf_token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch student details');
      }

      const data = await response.json();
      setStudentDetails(data);
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return { fetchDetails, isLoading, error, studentDetails };
};
